import { DialogRef } from '@angular/cdk/dialog';
import { CdkMenu, CdkMenuTrigger } from '@angular/cdk/menu';
import { CommonModule } from '@angular/common';
import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  model,
  viewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { MdwButton } from '@spaces-ui/meadow/components/button';
import { MdwIcon } from '@spaces-ui/meadow/icons';
import { DropdownArrow, Time_Duotone } from '@spaces-ui/meadow/icons/svgs';
import { DateUtils, Time } from '@spaces-ui/utils';

import { MdwTimeInput } from '../time-input/time-input';

@Component({
  selector: 'mdw-time-picker',
  templateUrl: './time-picker.html',
  styleUrls: ['./time-picker.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, MdwButton, MdwIcon, MdwTimeInput, CdkMenu, CdkMenuTrigger],
})
export class MdwTimePicker {
  private readonly destroyRef = inject(DestroyRef);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly cdkDialogRef = inject(DialogRef, { optional: true });

  public value = model<Time>();
  public label = input<string>();
  public placeholder = input<string>('Pick a time');
  public triggerType = input<'button' | 'input'>('button');

  private trigger = viewChild(CdkMenuTrigger);

  public timeValue = computed(() => {
    const value = this.value();

    if (!value) {
      return '';
    }

    return DateUtils.formatTime(value);
  });

  public icons = { Time_Duotone, DropdownArrow };

  public constructor() {
    afterNextRender(() => {
      const trigger = this.trigger();
      if (!trigger) {
        return;
      }

      const doc = this.host.nativeElement.ownerDocument;

      trigger.opened.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        if (this.cdkDialogRef) {
          this.cdkDialogRef.disableClose = true;
          this.cdkDialogRef.config.disableClose = true;
        }

        // Document-level CAPTURE listener fires before the dialog's own backdrop listener.
        const captureHandler = (ev: Event) => {
          const target = ev.target as Node | null;

          // If it's outside the menu & trigger, close the menu
          const panel = doc.querySelector('.cdk-overlay-pane:has([cdkMenu])');
          const inPanel = !!panel && panel.contains(target as Node);
          const inTrigger = this.host.nativeElement.contains(target as Node);
          if (!inPanel && !inTrigger) {
            trigger.close();
          }
        };

        // Capture-phase listeners so we run before the dialog’s own handlers.
        doc.addEventListener('pointerdown', captureHandler, true);
        doc.addEventListener('click', captureHandler, true);

        trigger.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
          setTimeout(() => {
            if (this.cdkDialogRef) {
              this.cdkDialogRef.disableClose = false;
              this.cdkDialogRef.config.disableClose = false;
            }
          }, 200);

          doc.removeEventListener('pointerdown', captureHandler, true);
          doc.removeEventListener('click', captureHandler, true);
        });
      });
    });
  }
}
